import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {change} from 'redux-form';
// @ts-expect-error
import type {FieldArrayProps} from 'redux-form';

import {getById} from 'nutshell-core/stagesets/stagesets-selectors';

import {Button} from 'shells/button/button';
import {DraggableList} from 'shells/draggable-row/draggable-list';
import type {ListItem as DraggableListItem} from 'shells/draggable-row/draggable-list';
import {reorder} from 'shells/draggable-row/helpers';
import {Illustration} from 'shells/illustrations/illustration';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {TwoRowItem} from 'shells/two-row-item/two-row-item';
import {Body} from 'shells/typography/body';

import {getFormSchema} from '../../../forms-selectors';

import {analyticsClient} from '../../../../../analytics-client';
import {PipelineSelectPicker} from '../../../../core/form/fields/pipeline-select-picker';

import {STAGESET_RULES_FIELD_NAME, FORM_NAME} from '../create-rules-constants';
import {DecisionFieldRule} from '../decision-field-rule';
import {useGetDecisionFields} from '../graphql/hooks/use-get-decision-fields';
import type {DecisionField} from '../graphql/hooks/use-get-decision-fields';
import type {StagesetRule} from '../types';

type Props = FieldArrayProps & {
    formId: string;
};

export function SpecialCases(props: Props) {
    const [isAddingDistributionRule, setIsAddingDistributionRule] = React.useState(false);
    const [selectedPipeline, setSelectedPipeline] = React.useState<any>(null);
    const [selectedFieldId, setSelectedFieldId] = React.useState<any>(null);
    const [selectedOptionId, setSelectedOptionId] = React.useState<any>(null);

    const {fields: decisionFields} = useGetDecisionFields(props.formId, 'lead');

    const stagesetsById = useSelector(getById);
    const formSchema = useSelector(getFormSchema);
    const dispatch = useDispatch();

    const canSave = selectedPipeline && selectedFieldId && selectedOptionId;
    const stagesetRuleComponents =
        props.fields && decisionFields
            ? getStagesetRuleComponents(props.fields, stagesetsById, decisionFields, formSchema)
            : [];

    const resetState = () => {
        setIsAddingDistributionRule(false);
        setSelectedFieldId(null);
        setSelectedOptionId(null);
        setSelectedPipeline(null);
    };

    return (
        <StackLegacy spacing={24}>
            <StackLegacy spacing={8}>
                <Body isBold={true}>Special cases</Body>
                <Body size='small'>
                    You can also put leads into a different pipeline based on the answer to any
                    multiple choice question. If no rules are met, the pipeline chosen above will be
                    applied.
                </Body>
            </StackLegacy>
            {stagesetRuleComponents && stagesetRuleComponents.length > 0 && (
                <DraggableList
                    listItems={stagesetRuleComponents}
                    handleReorder={(startIndex, endIndex) => {
                        const stagesetRulesToReorder = props.fields.getAll();
                        if (stagesetRulesToReorder) {
                            const reorderedStagesetRules = reorder(
                                stagesetRulesToReorder,
                                startIndex,
                                endIndex
                            );
                            dispatch(
                                change(FORM_NAME, STAGESET_RULES_FIELD_NAME, reorderedStagesetRules)
                            );
                        }
                    }}
                />
            )}
            {!isAddingDistributionRule && (
                <Button
                    onClick={() => setIsAddingDistributionRule(true)}
                    variant='text-primary'
                    size='big'
                    noPadding={true}
                >
                    + Add a distribution rule
                </Button>
            )}
            {isAddingDistributionRule && (
                <StackLegacy spacing={24}>
                    <DecisionFieldRule
                        formId={props.formId}
                        entityType='lead'
                        fieldCondition={{
                            title: 'If the answer to...',
                            placeholder: 'Select a question...',
                        }}
                        optionCondition={{
                            title: 'Is...',
                            placeholder: '-',
                        }}
                        onUpdate={(fieldId, optionId) => {
                            setSelectedFieldId(fieldId);
                            setSelectedOptionId(optionId);
                        }}
                        customNoOptionsMessage={{
                            icon: <Illustration src='multiple-choice-questions' width={150} />,
                            text: 'Your form doesn’t have any lead-based multiple choice questions. Once you add one, you’ll be able to set your special cases here.',
                        }}
                    />
                    {selectedFieldId && selectedOptionId && (
                        <StackLegacy spacing={12}>
                            <Body>Put leads into...</Body>
                            <PipelineSelectPicker
                                selectedPipeline={null}
                                onSelectPipeline={setSelectedPipeline}
                                useAutoDistribute={false}
                            />
                        </StackLegacy>
                    )}
                    <div className='flex gap-8 justify-end'>
                        <Button
                            size='big'
                            onClick={() => {
                                resetState();
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant='save'
                            size='big'
                            disabled={!canSave}
                            onClick={() => {
                                if (selectedFieldId && selectedOptionId && selectedPipeline) {
                                    const newStagesetRule: StagesetRule = {
                                        fieldId: selectedFieldId,
                                        answerChoiceId: selectedOptionId,
                                        stagesetId: selectedPipeline,
                                    };
                                    props.fields.push(newStagesetRule);
                                    analyticsClient.track(
                                        analyticsClient.EVENTS.TRACK_FORM_ANSWER_RULE_CREATED,
                                        {
                                            fieldType: 'lead',
                                            entityType: 'stagesets',
                                        }
                                    );
                                    resetState();
                                }
                            }}
                        >
                            Apply
                        </Button>
                    </div>
                </StackLegacy>
            )}
        </StackLegacy>
    );
}

function getStagesetRuleComponents(
    // @ts-expect-error
    fields,
    // @ts-expect-error
    stagesetsById,
    decisionFields: DecisionField[],
    // @ts-expect-error
    formSchema
): DraggableListItem[] {
    const stagesetRules: StagesetRule[] | null | undefined = fields.getAll();
    if (!stagesetRules) {
        return [];
    }

    const stagesetRuleComponents = [];
    for (let i = 0; i < stagesetRules.length; i++) {
        // @ts-expect-error
        const {fieldId, answerChoiceId, stagesetId} = stagesetRules[i];

        const stageset = stagesetsById[stagesetId];
        const decisionField = decisionFields.find(
            (dField) => dField.originalFieldApiId === fieldId || dField.id === fieldId
        );

        if (stageset && decisionField) {
            const fieldSchema = formSchema.properties[decisionField.attribute.id];
            // @ts-expect-error
            const option = fieldSchema.options.find((o) => o.id === answerChoiceId);

            stagesetRuleComponents.push({
                id: `${fieldId}-${answerChoiceId}-${stagesetId}`,
                component: (
                    <TwoRowItem
                        topRow={
                            <>
                                <Body isBold={true}>{decisionField.label}</Body>{' '}
                                <Body>is “{option.name}”</Body>
                            </>
                        }
                        bottomRow={
                            <Body size='small' color='light'>
                                {stageset.name}
                            </Body>
                        }
                        onRemove={() => {
                            fields.remove(i);
                        }}
                    />
                ),
            });
        }
    }

    return stagesetRuleComponents;
}
